import * as fa from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type React from "react";
import { type FC, useState } from "react";
import { copyToClipboard } from "../../util/copyToClipboard";
import { HoverHint } from "../content/HoverHint";

const maxWidthOptions = {
  sm: "max-w-xs",
  md: "max-w-md",
  lg: "max-w-lg",
  full: "max-w-full",
};
const minHeightOptions = {
  base: "",
  "300": "min-h-[300px]",
  "500": "min-h-[500px]",
  "700": "min-h-[700px]",
};
interface StringLikeInputProps {
  type: "text" | "email" | "password";
  label: string;
  value: string;
  onChange: (newValue: string) => void;
  placeholder?: string;
  focus?: boolean;
  readonly?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
  maxWidth?: keyof typeof maxWidthOptions;
  maxLength?: number;
}
interface TextAreaInputProps extends Omit<StringLikeInputProps, "type"> {
  type: "textarea";
  minHeight?: keyof typeof minHeightOptions;
}
interface NumberLikeInputProps {
  type: "number";
  label: string;
  value: number;
  onChange: (newValue: number) => void;
  placeholder?: string;
  focus?: boolean;
  readonly?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  maxWidth?: keyof typeof maxWidthOptions;
}
interface DateLikeInputProps {
  type: "date";
  label: string;
  value: Date;
  onChange: (newValue: Date) => void;
  placeholder?: string;
  focus?: boolean;
  readonly?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  maxWidth?: keyof typeof maxWidthOptions;
}
interface TimeLikeInputProps {
  type: "time";
  label: string;
  value: Date;
  onChange: (newValue: Date) => void;
  placeholder?: string;
  focus?: boolean;
  readonly?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  maxWidth?: keyof typeof maxWidthOptions;
}
type InputProps =
  | StringLikeInputProps
  | TextAreaInputProps
  | DateLikeInputProps
  | TimeLikeInputProps
  | NumberLikeInputProps;
export const Input: FC<InputProps> = (props) => {
  return (
    <label
      className={`flex flex-col text-sm font-medium text-brand-900 w-full ${maxWidthOptions[props.maxWidth ?? "md"]}`}
    >
      <span className={"flex items-center justify-between"}>
        {props.label}
        {"maxLength" in props && props.maxLength !== undefined && (
          <span className={"font-normal text-slate-500"}>
            {props.value.length}/{props.maxLength}
          </span>
        )}
      </span>
      <InputControl {...props} />
    </label>
  );
};

export const InputControl: FC<InputProps> = (props) => {
  if (props.type === "textarea") {
    return (
      <textarea
        autoFocus={props.focus}
        className={`border-2 border-slate-200 outline-brand-700 ${props.readonly ? "text-slate-500 cursor-not-allowed" : "text-black"} rounded-sm text-base bg-white font-normal px-2 py-2 mt-1 h-32 w-full ${minHeightOptions[props.minHeight ?? "base"]}`}
        placeholder={props.placeholder}
        value={props.value}
        readOnly={props.readonly}
        maxLength={props.maxLength}
        ref={props.inputRef as React.RefObject<HTMLTextAreaElement>}
        onChange={(e) => props.onChange(e.target.value)}
      />
    );
  }
  if (props.type === "date") {
    return (
      <input
        autoFocus={props.focus}
        type={"date"}
        className={`border-2 border-slate-200 outline-brand-700 ${props.readonly ? "text-slate-500 cursor-not-allowed" : "text-black"} rounded-sm text-base bg-white font-normal px-2 mt-1 h-10 w-full`}
        placeholder={props.placeholder}
        readOnly={props.readonly}
        ref={props.inputRef}
        onChange={(e) => props.onChange(new Date(e.target.value))}
        value={`${props.value.getFullYear()}-${String(props.value.getMonth() + 1).padStart(2, "0")}-${String(props.value.getDate()).padStart(2, "0")}`}
      />
    );
  }
  if (props.type === "number") {
    return (
      <input
        autoFocus={props.focus}
        type={"number"}
        className={`border-2 border-slate-200 outline-brand-700 ${props.readonly ? "text-slate-500 cursor-not-allowed" : "text-black"} rounded-sm text-base bg-white font-normal px-2 mt-1 h-10 w-full`}
        placeholder={props.placeholder}
        readOnly={props.readonly}
        ref={props.inputRef}
        onChange={(e) => props.onChange(Number(e.target.value))}
        value={`${props.value}`}
      />
    );
  }
  if (props.type === "time") {
    return (
      <input
        autoFocus={props.focus}
        type={"time"}
        className={`border-2 border-slate-200 outline-brand-700 ${props.readonly ? "text-slate-500 cursor-not-allowed" : "text-black"} rounded-sm text-base bg-white font-normal px-2 mt-1 h-10 w-full`}
        placeholder={props.placeholder}
        readOnly={props.readonly}
        ref={props.inputRef}
        onChange={(e) => {
          // The native time input only yields "HH:mm"; keep the date part of the current value
          // instead of building a Date on 1970-01-01, which would otherwise end up being saved.
          const [hours, minutes] = e.target.value.split(":").map(Number);
          if (Number.isNaN(hours) || Number.isNaN(minutes)) {
            return;
          }
          const newValue = new Date(props.value);
          newValue.setHours(hours, minutes, 0, 0);
          props.onChange(newValue);
        }}
        value={`${String(props.value.getHours()).padStart(2, "0")}:${String(props.value.getMinutes()).padStart(2, "0")}`}
      />
    );
  }

  return (
    <input
      autoFocus={props.focus}
      type={props.type}
      className={`border-2 border-slate-200 outline-brand-700 ${props.readonly ? "text-slate-500 cursor-not-allowed" : "text-black"} rounded-sm text-base bg-white font-normal px-2 mt-1 h-10 w-full`}
      placeholder={props.placeholder}
      value={props.value}
      readOnly={props.readonly}
      maxLength={props.maxLength}
      ref={props.inputRef as React.RefObject<HTMLInputElement>}
      onChange={(e) => props.onChange(e.target.value)}
    />
  );
};

export const CopyableInput: FC<Pick<StringLikeInputProps, "label" | "value">> = (props) => {
  const [justCopied, setJustCopied] = useState(false);
  const copy = async () => {
    await copyToClipboard(props.value);
    setJustCopied(true);
    setTimeout(() => setJustCopied(false), 1000);
  };
  return (
    <label className={"flex flex-col text-sm font-medium text-brand-900 w-full max-w-md"}>
      {props.label}
      <div className={"border-2 border-slate-200 outline-brand-700 rounded-sm mt-1 relative"}>
        <input
          type={"text"}
          readOnly={true}
          className={"text-black text-base bg-white font-normal h-10 px-2 w-full"}
          value={props.value}
        />
        <button
          className={"absolute top-1 right-1 h-8 w-8 rounded-sm hover:bg-slate-100"}
          onClick={() => copy()}
        >
          <HoverHint hint={"Copy to clipboard"}>
            <div className={"h-full w-full flex items-center justify-center"}>
              <FontAwesomeIcon
                icon={justCopied ? fa.faCheck : fa.faCopy}
                bounce={justCopied}
                className={"text-brand-800"}
              />
            </div>
          </HoverHint>
        </button>
      </div>
    </label>
  );
};
